(() => {
  'use strict';

  const SELECTOR = '[data-cut-builder-v2]';

  class CustomCutCalculatorV2 {
    constructor(builder) {
      this.builder = builder;
      this.sectionId = builder.dataset.sectionId;
      this.productInfo = builder.closest('product-info');
      this.form = document.getElementById(builder.dataset.productFormId);
      this.configElement = document.getElementById(`cut-pricing-config-v2-${this.sectionId}`);
      this.rowsElement = builder.querySelector('[data-cut-rows-v2]');
      this.template = document.getElementById(`CustomCutRowV2-${this.sectionId}`);
      this.messageElement = builder.querySelector('[data-cut-message-v2]');
      this.propertiesContainer = this.form?.querySelector('[data-v2-dynamic-properties]');
      this.materialElement = builder.querySelector('[data-material-total-v2]');
      this.cutChargeElement = builder.querySelector('[data-cut-charge-v2]');
      this.totalElement = builder.querySelector('[data-config-total-v2]');
      this.mainTotalElement = this.productInfo?.querySelector('#totalPriceV2');
      this.config = this.readConfig();
      this.lastResult = null;
      this.unsubscribeVariant = null;
      this.currentVariantAvailable = this.getSelectedVariantData()?.available !== false;

      if (!this.rowsElement || !this.template || !this.form || !this.configElement) {
        console.error('V2 custom-cut calculator could not initialize because required markup is missing.');
        return;
      }

      this.bindEvents();
      this.addRow();
      this.observeVariantPrice();
      this.subscribeToVariantChanges();
      this.calculate({ validate: false });
      builder.dataset.initialized = 'true';
    }

    readConfig() {
      const el = this.configElement;
      const number = (value, fallback = 0) => {
        const parsed = Number.parseFloat(value);
        return Number.isFinite(parsed) ? parsed : fallback;
      };

      const roundSet = String(el?.dataset.roundSet || '')
        .split(',')
        .map((value) => number(value, NaN))
        .filter(Number.isFinite)
        .sort((a, b) => a - b);

      const roundingOffset = number(el?.dataset.roundingOffset, 0.124);

      return {
        roundSet,
        maxLength: number(el?.dataset.maxL, 96),
        maxWidth: number(el?.dataset.maxW, 48),
        roundingOffset,
        // The same 0.124" value currently used by the V2 calculator is also
        // the saw-blade kerf used by the one-sheet packing validator.
        kerf: Math.max(0, number(el?.dataset.kerf, roundingOffset)),
        allowRotation: el?.dataset.allowRotation !== 'false',
        maxPackingPieces: Math.max(1, Math.floor(number(el?.dataset.maxPackingPieces, 500))),
        cutRates: {
          1: number(el?.dataset.cutPrice1, 10),
          2: number(el?.dataset.cutPrice2, 5),
          3: number(el?.dataset.cutPrice3, 4),
          4: number(el?.dataset.cutPrice4, 3),
          5: number(el?.dataset.cutPrice5, 2.5),
          sixPlus: number(el?.dataset.cutPrice6Plus, 2),
        },
      };
    }

    bindEvents() {
      this.builder.querySelector('[data-add-cut-v2]')?.addEventListener('click', () => this.addRow());
      this.builder.querySelector('[data-calculate-cuts-v2]')?.addEventListener('click', () => {
        this.calculate({ validate: true, focusError: true });
      });

      this.rowsElement.addEventListener('click', (event) => {
        const button = event.target.closest('[data-remove-cut-v2]');
        if (!button) return;
        const row = button.closest('[data-cut-row-v2]');
        if (!row) return;

        if (this.rowsElement.querySelectorAll('[data-cut-row-v2]').length === 1) {
          this.resetRow(row);
        } else {
          row.remove();
        }
        this.renumberRows();
        this.calculate({ validate: false });
      });

      const recalculate = () => this.calculate({ validate: false });
      this.rowsElement.addEventListener('input', recalculate);
      this.rowsElement.addEventListener('change', recalculate);

      this.form.addEventListener('submit', (event) => {
        const result = this.calculate({ validate: true, focusError: true });
        if (!result) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, true);

      this.productInfo?.querySelector('.qty__input')?.addEventListener('input', recalculate);
      this.productInfo?.querySelector('.qty__input')?.addEventListener('change', recalculate);

      const clearButton = this.productInfo?.querySelector('[data-cut-clear-v2]');
      clearButton?.addEventListener('click', () => this.clearAll());

      this.productInfo?.addEventListener('change', (event) => {
        if (event.target.closest('variant-selects') && !event.target.closest('[data-cut-builder-v2]')) {
          window.setTimeout(() => this.calculate({ validate: false }), 80);
        }
      });
    }

    subscribeToVariantChanges() {
      try {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
          this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (!data || data.sectionId === this.sectionId) {
              if (data?.variant) this.currentVariantAvailable = data.variant.available !== false;
              window.setTimeout(() => this.calculate({ validate: false }), 0);
            }
          });
        }
      } catch (error) {
        console.warn('V2 calculator could not subscribe to variant changes.', error);
      }
    }

    observeVariantPrice() {
      const priceContainer = this.productInfo?.querySelector(`#price-${CSS.escape(this.sectionId)}`);
      if (!priceContainer || !('MutationObserver' in window)) return;

      this.priceObserver = new MutationObserver(() => this.calculate({ validate: false }));
      this.priceObserver.observe(priceContainer, { childList: true, subtree: true, attributes: true });
    }

    addRow(values = {}) {
      const fragment = this.template.content.cloneNode(true);
      const row = fragment.querySelector('[data-cut-row-v2]');
      if (!row) return;

      row.querySelector('[data-cut-width-v2]').value = values.width ?? '';
      row.querySelector('[data-cut-length-v2]').value = values.length ?? '';
      row.querySelector('[data-cut-pieces-v2]').value = values.pieces ?? 1;
      row.querySelector('[data-cut-tolerance-v2]').value = values.tolerance ?? '';
      this.rowsElement.appendChild(fragment);
      this.renumberRows();
    }

    resetRow(row) {
      row.querySelector('[data-cut-width-v2]').value = '';
      row.querySelector('[data-cut-length-v2]').value = '';
      row.querySelector('[data-cut-pieces-v2]').value = '1';
      row.querySelector('[data-cut-tolerance-v2]').value = '';
      row.querySelectorAll('.input-error').forEach((input) => input.classList.remove('input-error'));
    }

    clearAll() {
      this.rowsElement.innerHTML = '';
      this.addRow();
      this.hideMessage();
      this.calculate({ validate: false });
    }

    renumberRows() {
      this.rowsElement.querySelectorAll('[data-cut-row-v2]').forEach((row, index) => {
        row.dataset.cutIndex = String(index + 1);
        const removeButton = row.querySelector('[data-remove-cut-v2]');
        if (removeButton) removeButton.setAttribute('aria-label', `Remove cut ${index + 1}`);
      });
    }

    hasCutIntent() {
      const rows = [...this.rowsElement.querySelectorAll('[data-cut-row-v2]')];

      return rows.some((row) => {
        const width = String(row.querySelector('[data-cut-width-v2]')?.value || '').trim();
        const length = String(row.querySelector('[data-cut-length-v2]')?.value || '').trim();
        const tolerance = String(row.querySelector('[data-cut-tolerance-v2]')?.value || '').trim();
        const pieces = String(row.querySelector('[data-cut-pieces-v2]')?.value || '').trim();

        // A new row starts with Pieces = 1. That default alone does not mean
        // the customer wants cuts; any other entered cut value does.
        return width !== '' || length !== '' || tolerance !== '' || (pieces !== '' && pieces !== '1');
      });
    }

    clearCutInputErrors() {
      this.rowsElement.querySelectorAll('.input-error').forEach((input) => input.classList.remove('input-error'));
    }

    getProductVariants() {
      const data = this.productInfo?.querySelector('variant-selects [data-product-variants-v2]');
      if (!data?.textContent.trim()) return [];

      try {
        const variants = JSON.parse(data.textContent);
        return Array.isArray(variants) ? variants : [];
      } catch (_) {
        return [];
      }
    }

    getSelectedOptionValues() {
      const variantSelects = this.productInfo?.querySelector('variant-selects');
      if (!variantSelects) return [];

      const controls = [...variantSelects.querySelectorAll('[name^="options["]')];
      const optionOrder = [];
      const selectedByName = new Map();

      controls.forEach((control) => {
        const name = this.getOptionName(control);
        if (!name) return;
        if (!optionOrder.includes(name)) optionOrder.push(name);

        if (control.matches('select')) {
          selectedByName.set(name, control.value);
        } else if (control.checked) {
          selectedByName.set(name, control.value);
        }
      });

      return optionOrder.map((name) => String(selectedByName.get(name) ?? ''));
    }

    getVariantFromSelectedOptions() {
      const selectedOptions = this.getSelectedOptionValues();
      if (!selectedOptions.length) return null;

      return this.getProductVariants().find((variant) => {
        if (!Array.isArray(variant?.options) || variant.options.length !== selectedOptions.length) return false;
        return variant.options.every((value, index) => String(value) === selectedOptions[index]);
      }) || null;
    }

    getUnitPrice() {
      // V2 full-sheet products always use the selected Shopify variant price
      // as the material price for one complete sheet.
      const matchedVariant = this.getVariantFromSelectedOptions();
      if (Number.isFinite(matchedVariant?.price)) return matchedVariant.price / 100;

      const visiblePrice = this.productInfo?.querySelector('.main_var_price[data-price]');
      const cents = Number(visiblePrice?.dataset.price);
      if (Number.isFinite(cents) && cents >= 0) return cents / 100;

      const selectedVariant = this.productInfo?.querySelector('variant-selects [data-selected-variant]');
      if (selectedVariant?.textContent.trim()) {
        try {
          const variant = JSON.parse(selectedVariant.textContent);
          if (Number.isFinite(variant?.price)) return variant.price / 100;
        } catch (_) {}
      }
      return 0;
    }

    getFullSheetMaterialPrice(_sheet) {
      // Every product using custom-products-v2 is a full-sheet product.
      // The Shopify variant price is already the price of one complete selected sheet.
      // Cut width/length are used only for fit validation and cut charges.
      const variantPrice = this.getUnitPrice();
      return Number.isFinite(variantPrice) && variantPrice >= 0 ? variantPrice : 0;
    }

    roundUpToSet(value, cap) {
      let number = Number.parseFloat(value);
      if (!Number.isFinite(number) || number <= 0) return 0;
      number = Math.min(number, cap);

      if (!this.config.roundSet.length) return number;
      for (const size of this.config.roundSet) {
        if (size > cap) break;
        if (number <= size - this.config.roundingOffset - 1e-9) return size;
      }

      // A future sheet may be longer/wider than the current breakpoint list.
      // Falling back to the requested dimension avoids using a smaller price size.
      return number;
    }

    getCutRate(pieces) {
      if (pieces >= 6) return this.config.cutRates.sixPlus;
      return this.config.cutRates[pieces] ?? this.config.cutRates.sixPlus;
    }

    getSelectedVariantData() {
      const selectedVariant = this.productInfo?.querySelector('variant-selects [data-selected-variant]');
      if (!selectedVariant?.textContent.trim()) return null;

      try {
        return JSON.parse(selectedVariant.textContent);
      } catch (_) {
        return null;
      }
    }

    getOptionName(input) {
      const name = String(input?.getAttribute('name') || '');
      const match = name.match(/^options\[(.*)]$/i);
      return match ? match[1].trim() : '';
    }

    isSheetSizeOptionName(name) {
      const normalized = String(name || '')
        .toLowerCase()
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      return normalized === 'size' || normalized === 'sheet size' || normalized.includes('sheet size');
    }

    parseImperialDimension(value) {
      const text = String(value || '')
        .replace(/[″”]/g, '"')
        .replace(/\b(?:inches|inch|in)\b/gi, '')
        .replace(/"/g, '')
        .trim();

      if (!text) return NaN;

      const mixed = text.match(/(\d+(?:\.\d+)?)\s*[- ]\s*(\d+)\s*\/\s*(\d+)/);
      if (mixed) {
        const whole = Number.parseFloat(mixed[1]);
        const numerator = Number.parseFloat(mixed[2]);
        const denominator = Number.parseFloat(mixed[3]);
        return denominator > 0 ? whole + (numerator / denominator) : NaN;
      }

      const fraction = text.match(/(\d+)\s*\/\s*(\d+)/);
      if (fraction) {
        const numerator = Number.parseFloat(fraction[1]);
        const denominator = Number.parseFloat(fraction[2]);
        return denominator > 0 ? numerator / denominator : NaN;
      }

      const decimal = text.match(/\d+(?:\.\d+)?/);
      return decimal ? Number.parseFloat(decimal[0]) : NaN;
    }

    parseSheetSize(value) {
      const raw = String(value || '').trim();
      if (!raw) return null;

      const normalized = raw.replace(/&quot;/gi, '"').replace(/[×]/g, 'x');
      const parts = normalized.split(/\s*(?:x|\bby\b)\s*/i);
      if (parts.length < 2) return null;

      const width = this.parseImperialDimension(parts[0]);
      const length = this.parseImperialDimension(parts[1]);
      if (!(width > 0 && length > 0)) return null;

      return { width, length, label: raw };
    }

    getSheetSize() {
      const fallback = {
        width: this.config.maxWidth,
        length: this.config.maxLength,
        label: `${this.cleanNumber(this.config.maxWidth)}\" x ${this.cleanNumber(this.config.maxLength)}\"`,
      };

      const inputs = [...(this.productInfo?.querySelectorAll('variant-selects [name^="options["]') || [])];
      const sizeInputs = inputs.filter((input) => this.isSheetSizeOptionName(this.getOptionName(input)));
      if (!sizeInputs.length) return fallback;

      let selectedInput = sizeInputs.find((input) => input.matches('select'));
      if (!selectedInput) selectedInput = sizeInputs.find((input) => input.checked) || sizeInputs[0];

      let value = '';
      if (selectedInput?.matches('select')) {
        value = selectedInput.selectedOptions?.[0]?.textContent || selectedInput.value;
      } else {
        value = selectedInput?.value || '';
      }

      return this.parseSheetSize(value) || fallback;
    }

    getToleranceDimensionAdjustment(tolerance) {
      const normalized = String(tolerance || '')
        .toLowerCase()
        .replace(/±/g, '+/-')
        .replace(/[″”]/g, '"')
        .replace(/\\/g, '')
        .replace(/\s+/g, ' ')
        .trim();

      // Labels can include extra text such as "- Nominal". The 1/8 fraction
      // alone triggers the 0.125" reduction used only for sheet-fit logic.
      return /1\s*\/\s*8/.test(normalized) ? 0.125 : 0;
    }

    getPackingDimensions(width, length, tolerance) {
      const adjustment = this.getToleranceDimensionAdjustment(tolerance);
      return {
        width: width - adjustment,
        length: length - adjustment,
        adjustment,
      };
    }

    getOversizeSupportMessage(sheet) {
      const selectedSheet = sheet?.label || '48" x 96"';
      return `These dimensions do not fit within the selected ${selectedSheet} sheet. For sizes larger than 48" x 96", please call 303-781-1171.`;
    }

    pieceFitsSheet(width, length, sheet) {
      const epsilon = 1e-7;

      if (!this.config.allowRotation) {
        return width <= sheet.width + epsilon && length <= sheet.length + epsilon;
      }

      // Width/Length labels do not lock the physical orientation. The long side may
      // be entered in either field, while the piece must still fit the same sheet.
      const pieceLongSide = Math.max(width, length);
      const pieceShortSide = Math.min(width, length);
      const sheetLongSide = Math.max(sheet.width, sheet.length);
      const sheetShortSide = Math.min(sheet.width, sheet.length);

      return pieceLongSide <= sheetLongSide + epsilon
        && pieceShortSide <= sheetShortSide + epsilon;
    }

    tryUniformGridPacking(pieces, binWidth, binHeight) {
      if (!pieces.length) return [];

      const epsilon = 1e-7;
      const first = pieces[0];
      const sameSize = pieces.every((piece) =>
        Math.abs(piece.width - first.width) <= epsilon
        && Math.abs(piece.height - first.height) <= epsilon
      );
      if (!sameSize) return null;

      const orientations = [{ width: first.width, height: first.height, rotated: false }];
      if (this.config.allowRotation && Math.abs(first.width - first.height) > epsilon) {
        orientations.push({ width: first.height, height: first.width, rotated: true });
      }

      let best = null;
      orientations.forEach((orientation) => {
        const columns = Math.floor((binWidth + epsilon) / orientation.width);
        const rows = Math.floor((binHeight + epsilon) / orientation.height);
        const capacity = columns * rows;
        if (capacity >= pieces.length && (!best || capacity > best.capacity)) {
          best = { ...orientation, columns, rows, capacity };
        }
      });

      if (!best) return null;

      return pieces.map((piece, index) => ({
        id: piece.id,
        cutNumber: piece.cutNumber,
        pieceNumber: piece.pieceNumber,
        x: (index % best.columns) * best.width,
        y: Math.floor(index / best.columns) * best.height,
        width: best.width,
        height: best.height,
        rotated: best.rotated,
      }));
    }

    checkSheetFit(cuts, sheet) {
      const kerf = this.config.kerf;
      const pieces = [];
      let totalPieces = 0;

      for (const cut of cuts) {
        totalPieces += cut.pieces;
        if (totalPieces > this.config.maxPackingPieces) {
          return {
            fits: false,
            reason: 'piece-limit',
            message: `The calculator can validate up to ${this.config.maxPackingPieces} pieces at one time. Please reduce the quantity or split this into separate orders.`,
          };
        }

        for (let pieceIndex = 0; pieceIndex < cut.pieces; pieceIndex += 1) {
          pieces.push({
            id: `${cut.number}-${pieceIndex + 1}`,
            cutNumber: cut.number,
            pieceNumber: pieceIndex + 1,
            nominalWidth: cut.width,
            nominalHeight: cut.length,
            packingWidth: cut.packingWidth,
            packingHeight: cut.packingLength,
            width: cut.packingWidth + kerf,
            height: cut.packingLength + kerf,
          });
        }
      }

      const binWidth = sheet.width + kerf;
      const binHeight = sheet.length + kerf;
      const requiredArea = pieces.reduce((sum, piece) => sum + (piece.width * piece.height), 0);
      const availableArea = binWidth * binHeight;

      if (requiredArea > availableArea + 1e-7) {
        return {
          fits: false,
          reason: 'area',
          message: this.buildFitErrorMessage(totalPieces, sheet),
        };
      }

      // Exact grid packing first for identical pieces, then fall back to MaxRects
      // for mixed cuts. Example: 12" x 12", +/- 1/8", 32 pieces on 48" x 96":
      // 12 - .125 + .124 = 11.999", so exactly 4 x 8 pieces fit.
      const packing = this.tryUniformGridPacking(pieces, binWidth, binHeight)
        || this.packRectangles(pieces, binWidth, binHeight);
      if (!packing) {
        return {
          fits: false,
          reason: 'packing',
          message: this.buildFitErrorMessage(totalPieces, sheet),
        };
      }

      return { fits: true, placements: packing, sheet, kerf, totalPieces };
    }

    buildFitErrorMessage(totalPieces, sheet) {
      const pieceWord = totalPieces === 1 ? 'piece' : 'pieces';
      return `These ${totalPieces} ${pieceWord} do not fit on one ${sheet.label} sheet after allowing for the ${this.cleanNumber(this.config.kerf)}\" blade kerf between pieces. Reduce the dimensions or number of pieces. For sizes larger than 48" x 96", please call 303-781-1171.`;
    }

    packRectangles(rectangles, binWidth, binHeight) {
      if (!rectangles.length) return [];

      const sorters = [
        (a, b) => (b.width * b.height) - (a.width * a.height),
        (a, b) => Math.max(b.width, b.height) - Math.max(a.width, a.height)
          || Math.min(b.width, b.height) - Math.min(a.width, a.height),
        (a, b) => b.width - a.width || b.height - a.height,
        (a, b) => b.height - a.height || b.width - a.width,
        (a, b) => (b.width + b.height) - (a.width + a.height),
      ];
      const heuristics = ['best-short-side', 'best-area', 'bottom-left', 'best-long-side'];
      const seenOrders = new Set();

      for (const sorter of sorters) {
        const ordered = [...rectangles].sort(sorter);
        const signature = ordered.map((piece) => piece.id).join('|');
        if (seenOrders.has(signature)) continue;
        seenOrders.add(signature);

        for (const heuristic of heuristics) {
          const placements = this.maxRectsPack(ordered, binWidth, binHeight, heuristic);
          if (placements) return placements;
        }
      }

      return null;
    }

    maxRectsPack(rectangles, binWidth, binHeight, heuristic) {
      let freeRectangles = [{ x: 0, y: 0, width: binWidth, height: binHeight }];
      const placements = [];

      for (const rectangle of rectangles) {
        const placement = this.findBestPlacement(rectangle, freeRectangles, heuristic);
        if (!placement) return null;

        placements.push({
          id: rectangle.id,
          cutNumber: rectangle.cutNumber,
          pieceNumber: rectangle.pieceNumber,
          x: placement.x,
          y: placement.y,
          width: placement.width,
          height: placement.height,
          rotated: placement.rotated,
        });

        freeRectangles = this.splitFreeRectangles(freeRectangles, placement);
      }

      return placements;
    }

    findBestPlacement(rectangle, freeRectangles, heuristic) {
      let best = null;

      for (const free of freeRectangles) {
        const orientations = [{ width: rectangle.width, height: rectangle.height, rotated: false }];
        if (this.config.allowRotation && Math.abs(rectangle.width - rectangle.height) > 1e-7) {
          orientations.push({ width: rectangle.height, height: rectangle.width, rotated: true });
        }

        for (const orientation of orientations) {
          if (orientation.width > free.width + 1e-7 || orientation.height > free.height + 1e-7) continue;

          const candidate = {
            x: free.x,
            y: free.y,
            width: orientation.width,
            height: orientation.height,
            rotated: orientation.rotated,
          };
          candidate.score = this.getPlacementScore(free, candidate, heuristic);

          if (!best || this.compareScores(candidate.score, best.score) < 0) best = candidate;
        }
      }

      return best;
    }

    getPlacementScore(free, used, heuristic) {
      const leftoverWidth = Math.max(0, free.width - used.width);
      const leftoverHeight = Math.max(0, free.height - used.height);
      const shortSide = Math.min(leftoverWidth, leftoverHeight);
      const longSide = Math.max(leftoverWidth, leftoverHeight);
      const area = Math.max(0, (free.width * free.height) - (used.width * used.height));
      const bottom = used.y + used.height;

      if (heuristic === 'best-area') return [area, shortSide, longSide, used.y, used.x];
      if (heuristic === 'bottom-left') return [bottom, used.x, shortSide, area, longSide];
      if (heuristic === 'best-long-side') return [longSide, shortSide, area, used.y, used.x];
      return [shortSide, longSide, area, used.y, used.x];
    }

    compareScores(a, b) {
      for (let index = 0; index < Math.max(a.length, b.length); index += 1) {
        const difference = (a[index] || 0) - (b[index] || 0);
        if (Math.abs(difference) > 1e-9) return difference;
      }
      return 0;
    }

    splitFreeRectangles(freeRectangles, used) {
      const next = [];
      const usedRight = used.x + used.width;
      const usedBottom = used.y + used.height;

      for (const free of freeRectangles) {
        const freeRight = free.x + free.width;
        const freeBottom = free.y + free.height;
        const intersects = !(
          used.x >= freeRight - 1e-9
          || usedRight <= free.x + 1e-9
          || used.y >= freeBottom - 1e-9
          || usedBottom <= free.y + 1e-9
        );

        if (!intersects) {
          next.push(free);
          continue;
        }

        if (used.x > free.x + 1e-9) {
          next.push({ x: free.x, y: free.y, width: used.x - free.x, height: free.height });
        }
        if (usedRight < freeRight - 1e-9) {
          next.push({ x: usedRight, y: free.y, width: freeRight - usedRight, height: free.height });
        }
        if (used.y > free.y + 1e-9) {
          next.push({ x: free.x, y: free.y, width: free.width, height: used.y - free.y });
        }
        if (usedBottom < freeBottom - 1e-9) {
          next.push({ x: free.x, y: usedBottom, width: free.width, height: freeBottom - usedBottom });
        }
      }

      return this.pruneFreeRectangles(next);
    }

    pruneFreeRectangles(rectangles) {
      const valid = rectangles.filter((rectangle) => rectangle.width > 1e-7 && rectangle.height > 1e-7);
      const keep = new Array(valid.length).fill(true);

      for (let i = 0; i < valid.length; i += 1) {
        if (!keep[i]) continue;
        for (let j = 0; j < valid.length; j += 1) {
          if (i === j || !keep[j]) continue;
          if (this.rectangleContains(valid[j], valid[i])) {
            keep[i] = false;
            break;
          }
        }
      }

      return valid.filter((_, index) => keep[index]);
    }

    rectangleContains(outer, inner) {
      return inner.x >= outer.x - 1e-9
        && inner.y >= outer.y - 1e-9
        && inner.x + inner.width <= outer.x + outer.width + 1e-9
        && inner.y + inner.height <= outer.y + outer.height + 1e-9;
    }

    readRows({ validate = false, sheet = this.getSheetSize() } = {}) {
      const rows = [...this.rowsElement.querySelectorAll('[data-cut-row-v2]')];
      let firstInvalid = null;
      const errors = [];

      const cuts = rows.map((row, index) => {
        const widthInput = row.querySelector('[data-cut-width-v2]');
        const lengthInput = row.querySelector('[data-cut-length-v2]');
        const piecesInput = row.querySelector('[data-cut-pieces-v2]');
        const toleranceInput = row.querySelector('[data-cut-tolerance-v2]');
        const width = Number.parseFloat(widthInput.value);
        const length = Number.parseFloat(lengthInput.value);
        const pieces = Number.parseInt(piecesInput.value, 10);
        const tolerance = toleranceInput.value.trim();
        const packing = (Number.isFinite(width) && Number.isFinite(length) && tolerance)
          ? this.getPackingDimensions(width, length, tolerance)
          : { width, length, adjustment: 0 };

        [widthInput, lengthInput, piecesInput, toleranceInput].forEach((input) => input.classList.remove('input-error'));

        const mark = (input, message) => {
          errors.push(`Cut ${index + 1}: ${message}`);
          firstInvalid ||= input;
          if (validate) input.classList.add('input-error');
        };

        if (!Number.isFinite(width) || width <= 0) mark(widthInput, 'enter a valid width.');
        if (!Number.isFinite(length) || length <= 0) mark(lengthInput, 'enter a valid length.');

        if (tolerance && Number.isFinite(packing.width) && Number.isFinite(packing.length)
          && (packing.width <= 0 || packing.length <= 0)) {
          mark(widthInput, 'the selected tolerance makes the finished cut dimension zero or less.');
          if (validate) lengthInput.classList.add('input-error');
        }

        if (tolerance && Number.isFinite(packing.width) && packing.width > 0
          && Number.isFinite(packing.length) && packing.length > 0
          && !this.pieceFitsSheet(packing.width, packing.length, sheet)) {
          mark(widthInput, this.getOversizeSupportMessage(sheet));
          widthInput.classList.add('input-error');
          lengthInput.classList.add('input-error');
        }

        if (!Number.isInteger(pieces) || pieces < 1) mark(piecesInput, 'pieces must be a whole number of 1 or more.');
        if (!tolerance) mark(toleranceInput, 'select a tolerance.');

        return {
          number: index + 1,
          width,
          length,
          pieces,
          tolerance,
          packingWidth: packing.width,
          packingLength: packing.length,
          toleranceAdjustment: packing.adjustment,
        };
      });

      return { cuts, errors, firstInvalid };
    }

    syncB2BDiscountTableForVariant() {
      const tableBody = this.productInfo?.querySelector('tbody[data-company_gid]');
      if (!tableBody) return;

      const companyId = tableBody.querySelector('.table-price-b2b')?.dataset.companyid || '';
      const variantId = String(this.productInfo?.querySelector('.product-variant-id')?.value || '');
      const entries = [...this.productInfo.querySelectorAll('.bulk_discount_entry')];
      const entry = entries.find((item) => item.dataset.company === companyId && item.dataset.variant === variantId);

      const defaults = {
        1: Number.parseFloat(tableBody.getAttribute('data-def-1-5')) || 0,
        2: Number.parseFloat(tableBody.getAttribute('data-def-6-10')) || 0,
        3: Number.parseFloat(tableBody.getAttribute('data-def-11-20')) || 0,
        4: Number.parseFloat(tableBody.getAttribute('data-def-20-plus')) || 0,
      };

      const discounts = entry ? {
        1: Number.parseFloat(entry.dataset.discount_for_1_to_5) || 0,
        2: Number.parseFloat(entry.dataset.discount_for_6_to_10) || 0,
        3: Number.parseFloat(entry.dataset.discount_for_11_to_20) || 0,
        4: Number.parseFloat(entry.dataset.discount_for_20) || 0,
      } : defaults;

      Object.entries(discounts).forEach(([position, percent]) => {
        const row = tableBody.querySelector(`.discount-table-row[data-pos="${position}"]`);
        const priceCell = row?.querySelector('.table-price-b2b');
        const savingsCell = row?.querySelector('.savings');
        if (priceCell) priceCell.dataset.off = String(percent);
        if (savingsCell) savingsCell.textContent = `(${percent}%)`;
      });
    }

    getDiscount(total) {
      this.syncB2BDiscountTableForVariant();
      const tableBody = this.productInfo?.querySelector('tbody[data-company_gid]');
      if (!tableBody) return { amount: total, percent: 0 };

      const quantity = Number.parseInt(this.productInfo.querySelector('.qty__input')?.value, 10) || 1;
      let position = 1;
      if (quantity > 5 && quantity <= 10) position = 2;
      else if (quantity > 10 && quantity < 20) position = 3;
      else if (quantity >= 20) position = 4;

      tableBody.querySelectorAll('.discount-table-row').forEach((row) => row.classList.remove('selected'));
      tableBody.querySelector(`.discount-table-row[data-pos="${position}"]`)?.classList.add('selected');

      tableBody.querySelectorAll('.discount-table-row').forEach((row) => {
        const percent = Number.parseFloat(row.querySelector('.table-price-b2b')?.dataset.off) || 0;
        const price = total - (total * percent / 100);
        const priceCell = row.querySelector('.table-price-b2b');
        if (priceCell) priceCell.textContent = this.formatMoney(price);
      });

      const selectedCell = tableBody.querySelector(`.discount-table-row[data-pos="${position}"] .table-price-b2b`);
      const percent = Number.parseFloat(selectedCell?.dataset.off) || 0;
      return { amount: total - (total * percent / 100), percent };
    }

    calculate({ validate = false, focusError = false } = {}) {
      const sheet = this.getSheetSize();

      // No cut dimensions entered = customer is buying one complete selected sheet.
      // Show the full-sheet material price immediately and do not require cut fields.
      if (!this.hasCutIntent()) {
        this.clearCutInputErrors();
        this.hideMessage();
        this.setAddToCartEnabled(true);

        const unitPrice = this.getUnitPrice();
        const materialTotal = this.getFullSheetMaterialPrice(sheet);
        const cutCharge = 0;
        const subtotal = materialTotal;
        const discounted = this.getDiscount(subtotal);
        const finalTotal = discounted.amount;
        const packing = {
          fits: true,
          placements: [],
          sheet,
          kerf: this.config.kerf,
          totalPieces: 0,
          fullSheet: true,
        };

        const result = {
          unitPrice,
          cuts: [],
          materialTotal,
          cutCharge,
          subtotal,
          discountPercent: discounted.percent,
          finalTotal,
          totalPieces: 0,
          sheet,
          packing,
          fullSheet: true,
        };

        this.lastResult = result;
        this.updateDisplay(result);
        this.updateProperties(result);
        return result;
      }

      const { cuts, errors, firstInvalid } = this.readRows({ validate, sheet });

      if (errors.length) {
        this.setAddToCartEnabled(false);
        this.resetCalculatedState();

        const persistentOversizeError = errors.find((error) =>
          error.includes('For sizes larger than 48" x 96"')
        );

        if (validate || persistentOversizeError) {
          this.showMessage(persistentOversizeError || errors[0]);
          if (validate && focusError && firstInvalid) {
            firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
            firstInvalid.focus({ preventScroll: true });
          }
        } else {
          this.hideMessage();
        }
        return null;
      }

      const packing = this.checkSheetFit(cuts, sheet);
      if (!packing.fits) {
        this.setAddToCartEnabled(false);
        this.resetCalculatedState();
        this.showMessage(packing.message);

        if (focusError) {
          this.builder.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return null;
      }

      this.hideMessage();
      this.setAddToCartEnabled(true);

      // Charge exactly one selected sheet at the selected Shopify variant price.
      // Cut dimensions affect only fit validation and cut charges.
      const unitPrice = this.getUnitPrice();
      const materialTotal = this.getFullSheetMaterialPrice(sheet);
      let cutCharge = 0;
      let totalPieces = 0;

      const calculatedCuts = cuts.map((cut) => {
        const billableWidth = cut.width;
        const billableLength = cut.length;
        const squareFeetPerPiece = (cut.width * cut.length) / 144;
        const materialPrice = 0;
        const cutRate = this.getCutRate(cut.pieces);
        const rowCutCharge = cutRate * cut.pieces;
        const rowTotal = rowCutCharge;

        cutCharge += rowCutCharge;
        totalPieces += cut.pieces;

        return { ...cut, billableWidth, billableLength, squareFeetPerPiece, materialPrice, cutRate, cutCharge: rowCutCharge, rowTotal };
      });

      const subtotal = materialTotal + cutCharge;
      const discounted = this.getDiscount(subtotal);
      const finalTotal = discounted.amount;

      const result = {
        unitPrice,
        cuts: calculatedCuts,
        materialTotal,
        cutCharge,
        subtotal,
        discountPercent: discounted.percent,
        finalTotal,
        totalPieces,
        sheet,
        packing,
      };
      this.lastResult = result;
      this.updateDisplay(result);
      this.updateProperties(result);
      return result;
    }

    resetCalculatedState() {
      this.lastResult = null;
      this.updateDisplay({ materialTotal: 0, cutCharge: 0, finalTotal: 0 });

      [
        '[data-v2-prop-total]',
        '[data-v2-prop-cut-charge]',
        '[data-v2-prop-cut-data]',
        '[data-v2-prop-cut-details]',
        '[data-v2-prop-cut-count]',
        '[data-v2-prop-total-pieces]',
        '[data-v2-prop-material-price]',
      ].forEach((selector) => {
        const input = this.form.querySelector(selector);
        if (input) input.value = '';
      });

      if (this.propertiesContainer) this.propertiesContainer.innerHTML = '';
    }

    setAddToCartEnabled(enabled) {
      const button = this.form.querySelector('button[type="submit"][name="add"]');
      if (!button) return;

      if (!enabled) {
        button.dataset.cutValidationBlocked = 'true';
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        return;
      }

      if (button.dataset.cutValidationBlocked !== 'true') return;
      delete button.dataset.cutValidationBlocked;

      const variantInput = this.form.querySelector('.product-variant-id');
      const canPurchase = this.currentVariantAvailable !== false
        && Boolean(variantInput?.value && !variantInput.disabled);

      if (canPurchase) {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      }
    }

    updateDisplay(result) {
      if (this.materialElement) this.materialElement.textContent = this.formatMoney(result.materialTotal);
      if (this.cutChargeElement) this.cutChargeElement.textContent = this.formatMoney(result.cutCharge);
      if (this.totalElement) this.totalElement.textContent = this.formatMoney(result.finalTotal);
      if (this.mainTotalElement) {
        this.mainTotalElement.textContent = this.formatMoney(result.finalTotal);
        this.mainTotalElement.dataset.total = result.finalTotal.toFixed(2);
      }

      const breakdown = this.builder.querySelector('[data-cut-breakdown-v2]');
      if (breakdown) breakdown.hidden = this.builder.dataset.showBreakdown === 'false';
    }

    updateProperties(result) {
      const set = (selector, value) => {
        const input = this.form.querySelector(selector);
        if (input) input.value = value;
      };

      const publicDetails = result.cuts.map((cut) =>
        `Cut ${cut.number}: ${this.cleanNumber(cut.width)}" W x ${this.cleanNumber(cut.length)}" L, ${cut.pieces} piece${cut.pieces === 1 ? '' : 's'}, ${cut.tolerance}`
      ).join(' | ');

      const data = result.cuts.map((cut) => ({
        number: cut.number,
        width: cut.width,
        length: cut.length,
        pieces: cut.pieces,
        tolerance: cut.tolerance,
        billableWidth: cut.billableWidth,
        billableLength: cut.billableLength,
        packingWidth: Number(cut.packingWidth.toFixed(3)),
        packingLength: Number(cut.packingLength.toFixed(3)),
        toleranceAdjustment: Number(cut.toleranceAdjustment.toFixed(3)),
        cutRate: Number(cut.cutRate.toFixed(2)),
      }));

      set('[data-v2-prop-total]', result.finalTotal.toFixed(2));
      set('[data-v2-prop-cut-charge]', result.cutCharge.toFixed(2));
      set('[data-v2-prop-cut-data]', JSON.stringify(data));
      set('[data-v2-prop-cut-details]', publicDetails);
      set('[data-v2-prop-cut-count]', result.cuts.length ? String(result.cuts.length) : '');
      set('[data-v2-prop-total-pieces]', result.cuts.length ? String(result.totalPieces) : '');
      set('[data-v2-prop-material-price]', result.materialTotal.toFixed(2));

      if (this.propertiesContainer) {
        this.propertiesContainer.innerHTML = '';
        const addProperty = (name, value) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${name}]`;
          input.value = value;
          this.propertiesContainer.appendChild(input);
        };

        result.cuts.forEach((cut) => {
          addProperty(`Cut ${cut.number} Details`, `${this.cleanNumber(cut.width)}" W x ${this.cleanNumber(cut.length)}" L`);
          addProperty(`Cut ${cut.number} Pieces`, String(cut.pieces));
          addProperty(`Cut ${cut.number} Tolerance`, cut.tolerance);
        });
      }
    }

    cleanNumber(value) {
      return Number.isFinite(value) ? String(Number(value.toFixed(3))) : '';
    }

    showMessage(message) {
      if (!this.messageElement) return;
      this.messageElement.textContent = message;
      this.messageElement.hidden = false;
    }

    hideMessage() {
      if (!this.messageElement) return;
      this.messageElement.textContent = '';
      this.messageElement.hidden = true;
    }

    formatMoney(amount) {
      try {
        if (window.Shopify && typeof Shopify.formatMoney === 'function') {
          const format = window.theme?.moneyFormat || '${{amount}}';
          return Shopify.formatMoney(Math.round((amount || 0) * 100), format);
        }
      } catch (_) {}

      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(amount || 0);
    }

    destroy() {
      this.unsubscribeVariant?.();
      this.priceObserver?.disconnect();
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((builder) => {
      if (builder.dataset.initialized === 'true') return;
      builder.customCutCalculatorV2 = new CustomCutCalculatorV2(builder);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(SELECTOR).forEach((builder) => builder.customCutCalculatorV2?.destroy());
  });
})();
